/**
 * Domain Registry - Dynamic Discovery
 *
 * Auto-detects all available ontology domains by scanning for domain.json files.
 * TTL files are loaded at build time via Vite's raw imports.
 */

import { getUrlLoadedDomains } from "./urlDomainRegistry.ts";

export interface DomainConfig {
    id: string;
    label: string;
    ttl: string;
    ns: string;
    disallowedImportNs?: string[];
    /** If true, this domain should be auto-loaded by the Workspace on initialization */
    autoloaded?: boolean;
    /** Additional domain namespaces to load alongside this domain (regardless of owl:imports) */
    dependencies?: string[];
    /** Example TTL files with sample data (filename -> TTL content) */
    exampleTtl?: Record<string, string>;
    /** Source file paths that were merged to create the TTL content */
    ttlFiles?: string[];
}

interface DomainMetadata {
    namespace: string;
    label?: string;
    ttlFiles: string[];
    disallowedImportNamespaces?: string[];
    /** If true, this domain should be auto-loaded by the Workspace on initialization */
    autoloaded?: boolean;
    /** Additional domain namespaces to load alongside this domain (regardless of owl:imports) */
    dependencies?: string[];
    /** Example TTL files with sample data (separate from schema files) */
    exampleTtlFiles?: string[];
}

// Discover all domain.json files
const domainMetadataModules = import.meta.glob<DomainMetadata>(
    '/public/ontologies/*/domain.json',
    { eager: true, import: 'default' }
);

// Load all TTL files as raw strings
const ttlModules = import.meta.glob<string>(
    '/public/ontologies/*/*.ttl',
    { eager: true, query: '?raw', import: 'default' }
);

// Build registry
const domains: DomainConfig[] = [];

for (const [metaPath, metadata] of Object.entries(domainMetadataModules)) {
    const match = metaPath.match(/^\/public\/ontologies\/([^/]+)\/domain\.json$/);
    if (!match) continue;

    const id = match[1];
    const domainFolder = `/public/ontologies/${id}`;

    // Merge TTL files
    const ttlParts: string[] = [];
    for (const ttlFile of metadata.ttlFiles) {
        const ttlPath = `${domainFolder}/${ttlFile}`;
        const ttlContent = ttlModules[ttlPath];
        if (ttlContent) {
            ttlParts.push(ttlContent);
        } else {
            console.warn(`TTL file not found: ${ttlPath}`);
        }
    }

    // Generate label from id if not provided
    const label = metadata.label ?? id
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    // Load example TTL files separately (not merged into schema)
    const exampleTtl: Record<string, string> = {};
    if (metadata.exampleTtlFiles) {
        for (const exampleFile of metadata.exampleTtlFiles) {
            const examplePath = `${domainFolder}/${exampleFile}`;
            const exampleContent = ttlModules[examplePath];
            if (exampleContent) {
                exampleTtl[exampleFile] = exampleContent;
            } else {
                console.warn(`Example TTL file not found: ${examplePath}`);
            }
        }
    }

    // Build list of full file paths for display
    const fullTtlPaths = metadata.ttlFiles.map(file => `${domainFolder}/${file}`);

    domains.push({
        id,
        label,
        ttl: ttlParts.join('\n\n'),
        ns: metadata.namespace,
        disallowedImportNs: metadata.disallowedImportNamespaces,
        autoloaded: metadata.autoloaded,
        dependencies: metadata.dependencies,
        exampleTtl: Object.keys(exampleTtl).length > 0 ? exampleTtl : undefined,
        ttlFiles: fullTtlPaths
    });
}

domains.sort((a, b) => a.label.localeCompare(b.label));

export const availableDomains: DomainConfig[] = domains;

export function getDomainById(id: string): DomainConfig | undefined {
    return domains.find(d => d.id === id);
}

export function getDefaultDomain(): DomainConfig {
    if (domains.length === 0) {
        throw new Error('No ontology domains found in /ontologies folder');
    }
    return domains[0];
}

/**
 * Get a domain by its namespace IRI.
 * Used by Workspace to resolve imported ontologies.
 *
 * @param ns - The namespace IRI to look up
 * @returns The DomainConfig if found, undefined otherwise
 */
export function getDomainByNamespace(ns: string): DomainConfig | undefined {
    return domains.find(d => d.ns === ns);
}

/**
 * Get all domains that are marked as autoloaded.
 * These domains should be automatically loaded by the Workspace on initialization.
 *
 * @returns Array of DomainConfig instances with autoloaded=true
 */
export function getAutoloadedDomains(): DomainConfig[] {
    return domains.filter(d => d.autoloaded === true);
}

/**
 * Get all available domains (static build-time + URL-loaded runtime domains).
 * Results are sorted alphabetically by label.
 *
 * @returns Combined array of all domain configs
 */
export function getAllAvailableDomains(): DomainConfig[] {
    const urlDomains = getUrlLoadedDomains();
    return [...domains, ...urlDomains].sort((a, b) => a.label.localeCompare(b.label));
}
